const STATUS_VALUES = ['available', 'occupied', 'maintenance', 'inactive'];

export const BUSINESS_TYPE_OPTIONS = [
  { value: 'marriage_hall', label: 'Marriage Hall' },
  { value: 'banquet_hall', label: 'Banquet Hall' },
  { value: 'conference_hall', label: 'Conference Hall' },
  { value: 'event_venue', label: 'Event Venue' },
];

const BUSINESS_TYPE_LABELS = BUSINESS_TYPE_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

export function getBusinessTypeLabel(value) {
  return BUSINESS_TYPE_LABELS[value] || String(value || '—').replace(/_/g, ' ');
}

export function getBusinessTypesLabel(values = []) {
  const list = Array.isArray(values) ? values : [];
  if (!list.length) return '—';
  return list.map((value) => getBusinessTypeLabel(value)).join(', ');
}

export function getHallCardStatus(hall) {
  const isActive = hall?.is_active !== false;
  const masterStatus = String(hall?.status || '').toLowerCase();
  if (!isActive || masterStatus === 'inactive') {
    return 'Inactive';
  }
  const availability = String(hall?.availability_status || '').trim();
  if (availability) {
    return availability;
  }
  if (masterStatus === 'occupied') return 'Occupied';
  if (masterStatus === 'maintenance') return 'Maintenance';
  return 'Available';
}

export function isHallBookable(hall) {
  return hall?.is_active !== false
    && String(hall?.status || '').toLowerCase() !== 'inactive'
    && String(hall?.availability_status || '').toLowerCase() !== 'inactive';
}

export function getHallHourlyAmount(hall) {
  const pricing = hall?.pricing || hall?.pricing_info || {};
  return Number(pricing.hourly_rate || hall?.hourly_rate || 0) || 0;
}

export function getHallFullDayAmount(hall) {
  const pricing = hall?.pricing || hall?.pricing_info || {};
  return Number(pricing.full_day_rate || hall?.full_day_rate || 0) || 0;
}

export function getHallDefaultAmount(hall) {
  return getHallFullDayAmount(hall) || getHallHourlyAmount(hall) || 0;
}

export function getHallDayBookingHours(hall) {
  if (hall?.is_24_hours || hall?.operating_hours?.is_24_hours) {
    return { start: '00:00', end: '23:59' };
  }
  const open = hall?.operating_hours?.open || hall?.open_time || '09:00';
  const close = hall?.operating_hours?.close || hall?.close_time || '23:00';
  return {
    start: String(open).slice(0, 5) || '09:00',
    end: String(close).slice(0, 5) || '23:00',
  };
}

function resolveBusinessTypes(business = {}) {
  if (Array.isArray(business.business_types) && business.business_types.length) {
    return business.business_types.map((item) => String(item || '').trim()).filter(Boolean);
  }
  const legacy = String(business.business_type || '').trim();
  return legacy ? [legacy] : ['marriage_hall'];
}

export function normalizeHall(hall) {
  const pricing = hall.pricing_info || {};
  const business = hall.business_info || {};
  const contact = hall.contact_info || {};
  const layout = hall.layout_info || {};
  const workingHours = hall.working_hours || {};
  const rawStatus = String(hall.status || '').toLowerCase();
  const mappedStatus = rawStatus === 'active' ? 'available' : rawStatus;
  const isActive = hall.is_active !== false;
  const resolvedStatus = !isActive || mappedStatus === 'inactive'
    ? 'inactive'
    : (STATUS_VALUES.includes(mappedStatus) ? mappedStatus : 'available');
  const businessTypes = resolveBusinessTypes(business);
  const is24Hours = Boolean(workingHours.is_24_hours);
  return {
    ...hall,
    is_active: isActive,
    hall_type: hall.hall_type || pricing.hall_type || 'Wedding',
    description: hall.description || 'No description added yet.',
    code: hall.code || '',
    hall_code: hall.code || `H-${hall.id}`,
    hall_images: Array.isArray(hall.hall_images) ? hall.hall_images : [],
    thumbnail: (
      (Array.isArray(hall.hall_images) && hall.hall_images[0]?.url)
      || hall.hall_image_url
      || ''
    ),
    hall_image_files: [],
    removed_image_ids: [],
    status: resolvedStatus,
    availability_status: hall.availability_status || getHallCardStatus({ ...hall, status: resolvedStatus }),
    today_bookings: Number(hall.today_bookings || 0),
    manager_name: contact.manager_name || '',
    phone: contact.phone || '',
    email: contact.email || '',
    business_types: businessTypes,
    business_type: businessTypes[0] || 'marriage_hall',
    business_type_label: getBusinessTypesLabel(businessTypes),
    capacity: Number(hall.capacity || 0),
    banquet_style_capacity: Number(layout.banquet_style_capacity || 0),
    theatre_style_capacity: Number(layout.theatre_style_capacity || 0),
    dining_capacity: Number(layout.dining_capacity || 0),
    floor_area: layout.floor_area || '',
    floor: layout.floor || '',
    min_guests: Number(layout.minimum_guests || Math.max(25, Math.round(Number(hall.capacity || 0) * 0.25))),
    max_guests: Number(layout.maximum_guests || hall.capacity || 0),
    dimensions: layout.dimensions || '',
    booking_interval: Number(workingHours.booking_interval || 60),
    is_24_hours: is24Hours,
    operating_hours: {
      open: is24Hours ? '00:00' : (workingHours.open_time || '09:00'),
      close: is24Hours ? '23:59' : (workingHours.close_time || '23:00'),
    },
    pricing: {
      hourly_rate: Number(pricing.hourly_rate || hall.hourly_rate || 0),
      full_day_rate: Number(pricing.full_day_rate || hall.full_day_rate || 0),
    },
    amenities: hall.amenities?.items || hall.amenities || [],
    availability: {
      blocked_dates: hall.availability?.blocked_dates || [],
      maintenance_dates: hall.availability?.maintenance_dates || [],
      operational_days: hall.availability?.operational_days || ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    },
    documents: hall.documents || [],
  };
}
